import * as React from 'react';
import { cn } from '../../lib/utils';

interface CopyButtonProps {
  value: string | null;
  label?: string;
  title?: string;
  className?: string;
}

export const writeClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
};

export const CopyButton = ({
  value,
  label = 'Copy',
  title = 'Copy the full page URL',
  className,
}: CopyButtonProps) => {
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (!done) return undefined;
    const t = window.setTimeout(() => setDone(false), 1400);
    return () => window.clearTimeout(t);
  }, [done]);

  return (
    <button
      type="button"
      disabled={!value}
      title={value ? `${title} — ${value}` : title}
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (value && (await writeClipboard(value))) setDone(true);
      }}
      className={cn(
        'shrink-0 rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold uppercase tracking-wide transition disabled:opacity-40',
        done
          ? 'bg-accent text-white'
          : 'bg-line/60 text-muted hover:bg-accent-soft hover:text-accent',
        className
      )}
    >
      {done ? '✓ Copied' : label}
    </button>
  );
};

/**
 * Inline copy icon (no icon library — task 021-09 ruling #6).
 */
const CopyIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

/**
 * Inline check icon shown while the copied feedback state is active.
 */
const CheckIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

interface CopyIconButtonProps {
  value: string | null;
  title?: string;
  className?: string;
}

/**
 * Icon-only variant of CopyButton — same clipboard action (shared
 * writeClipboard) with visible copied feedback via an icon swap
 * (copy → check for ~1400 ms). Task 021-09 ruling #6.
 */
export const CopyIconButton = ({
  value,
  title = 'Copy the full page URL',
  className,
}: CopyIconButtonProps) => {
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (!done) return undefined;
    const t = window.setTimeout(() => setDone(false), 1400);
    return () => window.clearTimeout(t);
  }, [done]);

  return (
    <button
      type="button"
      disabled={!value}
      aria-label={done ? 'Copied' : 'Copy page URL'}
      title={value ? `${title} — ${value}` : title}
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (value && (await writeClipboard(value))) setDone(true);
      }}
      className={cn(
        'shrink-0 rounded-md p-1 transition disabled:opacity-40',
        done
          ? 'bg-accent text-white'
          : 'bg-line/60 text-muted hover:bg-accent-soft hover:text-accent',
        className
      )}
    >
      {done ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
};
